/* FamilyMealCard — iter424. Multi-user семейни профили + AI семейно ястие. */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Plus, X, ChefHat, Loader2, Sparkles, Trash2, Edit3 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const RELATION_OPTIONS = [
  { v: "партньор",  bg: "партньор" },
  { v: "съпруга",   bg: "съпруга" },
  { v: "съпруг",    bg: "съпруг" },
  { v: "син",       bg: "син" },
  { v: "дъщеря",    bg: "дъщеря" },
  { v: "баща",      bg: "баща" },
  { v: "майка",     bg: "майка" },
  { v: "брат",      bg: "брат" },
  { v: "сестра",    bg: "сестра" },
];

const initialForm = {
  id: null, name: "", relation: "партньор", age: 35,
  sex: "other", weight_kg: 70, height_cm: 170, activity: "moderate",
  conditions: "", allergies: "",
};

export default function FamilyMealCard() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [members, setMembers] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [meal, setMeal] = useState(null);
  const [mealType, setMealType] = useState("dinner");
  const [cuisine, setCuisine] = useState("");
  const [composing, setComposing] = useState(false);

  const fetchMembers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/family/members`);
      setMembers(r.data?.members || []);
    } catch (_e) {}
  }, [API]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error(bg ? "Името е задължително" : "Name required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        member: {
          ...form,
          name: form.name.trim(),
          age: Number(form.age) || 30,
          weight_kg: Number(form.weight_kg) || null,
          height_cm: Number(form.height_cm) || null,
          conditions: form.conditions ? form.conditions.split(",").map((s) => s.trim()).filter(Boolean) : [],
          allergies:  form.allergies  ? form.allergies.split(",").map((s) => s.trim()).filter(Boolean) : [],
        },
      };
      await axios.post(`${API}/family/members`, payload);
      toast.success(bg ? "Запазено ✓" : "Saved ✓");
      setAdding(false);
      setForm(initialForm);
      fetchMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/family/members/${id}`);
      toast.success(bg ? "Изтрит ✓" : "Removed ✓");
      fetchMembers();
    } catch (_e) {
      toast.error(bg ? "Грешка" : "Failed");
    }
  };

  const handleEdit = (m) => {
    setForm({
      id: m.id, name: m.name, relation: m.relation, age: m.age,
      sex: m.sex, weight_kg: m.weight_kg || 70, height_cm: m.height_cm || 170,
      activity: m.activity || "moderate",
      conditions: (m.conditions || []).join(", "),
      allergies:  (m.allergies  || []).join(", "),
    });
    setAdding(true);
  };

  const handleCompose = async () => {
    if (members.length === 0) {
      toast.error(bg ? "Първо добавете членове" : "Add members first");
      return;
    }
    setComposing(true);
    setMeal(null);
    try {
      const r = await axios.post(`${API}/family/compose-meal`, {
        meal_type: mealType,
        cuisine_pref: cuisine.trim() || null,
      });
      setMeal(r.data);
      toast.success(bg ? "Семейно ястие готово ✓" : "Family meal ready ✓");
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally { setComposing(false); }
  };

  return (
    <Card className={`border ${isDark ? "border-teal-500/30 bg-gradient-to-br from-teal-500/5 to-emerald-500/5" : "border-teal-200 bg-gradient-to-br from-teal-50/40 to-emerald-50/30"}`}
          data-testid="family-meal-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-500 text-white">
            <Users className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Family Meal Sync" : "Family Meal Sync"}
            </h3>
            <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg
                ? "Семейни профили → AI генерира 1 ястие, което покрива целите на всички."
                : "Family profiles → AI plans 1 meal for everyone's targets."}
            </p>
          </div>
        </div>

        {/* Members list */}
        {members.length > 0 && (
          <div className="space-y-1.5" data-testid="family-members-list">
            {members.map((m) => (
              <div key={m.id} className={`p-2 rounded-lg flex items-center gap-2 ${isDark ? "bg-slate-900/40 border border-slate-700" : "bg-white border border-slate-200"}`}>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {m.name} <span className={`text-[10px] font-normal ${isDark ? "text-slate-400" : "text-slate-500"}`}>· {m.relation}, {m.age}г</span>
                  </p>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    <span className={`text-[9.5px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-orange-500/20 text-orange-200" : "bg-orange-50 text-orange-700"}`}>DIL {m.dil_target_g}g</span>
                    <span className={`text-[9.5px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-rose-500/20 text-rose-200" : "bg-rose-50 text-rose-700"}`}>{Math.round(m.kcal_target)} kcal</span>
                    {(m.conditions || []).slice(0, 2).map((c, i) => (
                      <span key={i} className={`text-[9.5px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-violet-500/20 text-violet-200" : "bg-violet-50 text-violet-700"}`}>{c}</span>
                    ))}
                    {(m.allergies || []).slice(0, 2).map((a, i) => (
                      <span key={i} className={`text-[9.5px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-amber-500/20 text-amber-200" : "bg-amber-50 text-amber-700"}`}>⚠ {a}</span>
                    ))}
                  </div>
                </div>
                <button onClick={() => handleEdit(m)} data-testid={`family-edit-${m.id}`}
                        className={`w-6 h-6 rounded flex items-center justify-center ${isDark ? "hover:bg-slate-800 text-slate-400" : "hover:bg-slate-100 text-slate-500"}`}>
                  <Edit3 className="w-3 h-3" />
                </button>
                <button onClick={() => handleDelete(m.id)} data-testid={`family-delete-${m.id}`}
                        className={`w-6 h-6 rounded flex items-center justify-center ${isDark ? "hover:bg-rose-500/20 text-rose-300" : "hover:bg-rose-100 text-rose-600"}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add/edit form */}
        {adding ? (
          <div className={`p-3 rounded-xl border space-y-2 ${isDark ? "border-teal-500/30 bg-teal-500/5" : "border-teal-200 bg-teal-50/40"}`} data-testid="family-add-form">
            <p className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {form.id ? (bg ? "Редактирай член" : "Edit member") : (bg ? "Добави член" : "Add member")}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder={bg ? "Име" : "Name"} data-testid="family-form-name"
                     className={`text-xs ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white border-slate-300"}`} />
              <select value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })}
                      data-testid="family-form-relation"
                      className={`text-xs h-9 rounded-md px-2 ${isDark ? "bg-slate-800 text-white border border-slate-700" : "bg-white border border-slate-300"}`}>
                {RELATION_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.bg}</option>)}
              </select>
              <Input type="number" min={2} max={120} value={form.age}
                     onChange={(e) => setForm({ ...form, age: e.target.value })}
                     placeholder={bg ? "Възраст" : "Age"} data-testid="family-form-age"
                     className={`text-xs ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white border-slate-300"}`} />
              <select value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })}
                      data-testid="family-form-sex"
                      className={`text-xs h-9 rounded-md px-2 ${isDark ? "bg-slate-800 text-white border border-slate-700" : "bg-white border border-slate-300"}`}>
                <option value="male">{bg ? "мъж" : "male"}</option>
                <option value="female">{bg ? "жена" : "female"}</option>
                <option value="other">{bg ? "друго" : "other"}</option>
              </select>
              <Input type="number" min={1} max={300} value={form.weight_kg}
                     onChange={(e) => setForm({ ...form, weight_kg: e.target.value })}
                     placeholder="kg" className={`text-xs ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white border-slate-300"}`} />
              <Input type="number" min={1} max={250} value={form.height_cm}
                     onChange={(e) => setForm({ ...form, height_cm: e.target.value })}
                     placeholder="cm" className={`text-xs ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white border-slate-300"}`} />
            </div>
            <Input value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })}
                   placeholder={bg ? "Състояния (разделени със ,)" : "Conditions (comma)"}
                   className={`text-xs ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white border-slate-300"}`} />
            <Input value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })}
                   placeholder={bg ? "Алергии (разделени със ,)" : "Allergies (comma)"}
                   className={`text-xs ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white border-slate-300"}`} />
            <div className="flex gap-2">
              <Button onClick={() => { setAdding(false); setForm(initialForm); }} variant="ghost"
                      className="flex-1" data-testid="family-form-cancel">{bg ? "Откажи" : "Cancel"}</Button>
              <Button onClick={handleSave} disabled={saving}
                      className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-500 text-white"
                      data-testid="family-form-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (bg ? "Запази" : "Save")}
              </Button>
            </div>
          </div>
        ) : (
          <Button onClick={() => setAdding(true)}
                  className={`w-full text-xs ${isDark ? "bg-teal-500/15 text-teal-200 hover:bg-teal-500/25" : "bg-teal-50 text-teal-800 hover:bg-teal-100 border border-teal-200"}`}
                  data-testid="family-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />{bg ? "Добави член" : "Add member"}
          </Button>
        )}

        {/* Compose family meal */}
        {members.length > 0 && (
          <div className={`p-3 rounded-xl border ${isDark ? "border-teal-500/30 bg-teal-500/5" : "border-teal-200 bg-teal-50/40"}`}>
            <p className={`text-xs font-bold mb-2 ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Семейно ястие за…" : "Family meal for…"}
            </p>
            <div className="flex gap-1.5 mb-2">
              {["breakfast", "lunch", "dinner", "snack"].map((mk) => {
                const labels = { breakfast: bg ? "Закуска" : "Breakfast", lunch: bg ? "Обяд" : "Lunch", dinner: bg ? "Вечеря" : "Dinner", snack: bg ? "Снак" : "Snack" };
                return (
                  <button key={mk} onClick={() => setMealType(mk)}
                          data-testid={`family-meal-${mk}`}
                          className={`flex-1 text-[10.5px] py-1.5 rounded-lg font-medium transition-colors ${
                            mealType === mk
                              ? "bg-gradient-to-r from-teal-500 to-emerald-500 text-white"
                              : (isDark ? "bg-slate-800 text-slate-300" : "bg-white text-slate-700 border border-slate-200")
                          }`}>{labels[mk]}</button>
                );
              })}
            </div>
            <Input value={cuisine} onChange={(e) => setCuisine(e.target.value)}
                   placeholder={bg ? "Кухня (опц.) — напр. средиземноморска" : "Cuisine (optional)"}
                   data-testid="family-cuisine-input"
                   className={`text-xs mb-2 ${isDark ? "bg-slate-800 text-white border-slate-700" : "bg-white border-slate-300"}`} />
            <Button onClick={handleCompose} disabled={composing}
                    className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white"
                    data-testid="family-compose-btn">
              {composing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              {bg ? "Генерирай семейно ястие" : "Compose family meal"}
            </Button>
          </div>
        )}

        {/* Composed meal */}
        {meal?.composed_meal && (
          <div className={`p-3 rounded-xl border ${isDark ? "border-emerald-500/30 bg-emerald-500/5" : "border-emerald-200 bg-emerald-50/40"}`}
               data-testid="family-meal-result">
            <div className="flex items-start gap-2">
              <ChefHat className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? "text-emerald-300" : "text-emerald-700"}`} />
              <div className="flex-1">
                <p className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {meal.composed_meal.dish_name_bg}
                </p>
                <p className={`text-[11px] mt-0.5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  {meal.composed_meal.description_bg}
                </p>
              </div>
            </div>
            <div className="flex gap-1.5 mt-2">
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-orange-500/20 text-orange-200" : "bg-orange-50 text-orange-700"}`}>
                DIL {meal.composed_meal.estimated_dil_g_per_portion}g/порция
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? "bg-rose-500/20 text-rose-200" : "bg-rose-50 text-rose-700"}`}>
                {meal.composed_meal.estimated_kcal_per_portion} kcal/порция
              </span>
            </div>
            {/* Components */}
            {Array.isArray(meal.composed_meal.components) && meal.composed_meal.components.length > 0 && (
              <div className="mt-2">
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                  {bg ? "Съставки" : "Components"}
                </p>
                <ul className={`text-[10.5px] space-y-0.5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  {meal.composed_meal.components.map((c, i) => (
                    <li key={i}>• <strong>{c.food}</strong> — {c.total_grams}g total{c.portions_bg && <span className={`italic ${isDark ? "text-slate-500" : "text-slate-500"}`}> ({c.portions_bg})</span>}</li>
                  ))}
                </ul>
              </div>
            )}
            {/* Rationale */}
            {meal.composed_meal.rationale_bg && (
              <p className={`text-[10.5px] italic mt-2 leading-relaxed ${isDark ? "text-emerald-200" : "text-emerald-900"}`}>
                💡 {meal.composed_meal.rationale_bg}
              </p>
            )}
            {/* Modifications */}
            {Array.isArray(meal.composed_meal.modifications_bg) && meal.composed_meal.modifications_bg.length > 0 && (
              <div className={`mt-2 p-2 rounded-lg ${isDark ? "bg-amber-500/10" : "bg-amber-50/60"}`}>
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-0.5 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                  {bg ? "Индивидуални модификации" : "Individual modifications"}
                </p>
                <ul className={`text-[10px] space-y-0.5 ${isDark ? "text-amber-100" : "text-amber-900"}`}>
                  {meal.composed_meal.modifications_bg.map((m, i) => <li key={i}>• {m}</li>)}
                </ul>
              </div>
            )}
            {/* Prep steps */}
            {Array.isArray(meal.composed_meal.prep_steps_bg) && meal.composed_meal.prep_steps_bg.length > 0 && (
              <div className="mt-2">
                <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                  {bg ? "Приготвяне" : "Preparation"}
                </p>
                <ol className={`text-[10.5px] space-y-0.5 list-decimal list-inside ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                  {meal.composed_meal.prep_steps_bg.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
